import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();

  useEffect(() => {
    const loadUser = async () => {
      // Add small delay to allow offline detection to stabilize
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        const cachedUser = localStorage.getItem('cached_user_profile');
        if (!navigator.onLine && cachedUser) {
          try {
            const parsed = JSON.parse(cachedUser);
            setUser(parsed);
            const languageToUse = parsed.preferred_language || 'en';
            i18n.changeLanguage(languageToUse);
            if (languageToUse === 'ar') {
              document.documentElement.setAttribute('dir', 'rtl');
            } else {
              document.documentElement.setAttribute('dir', 'ltr');
            }
          } catch (e) {
            // ignore parse errors
          }
          setLoading(false);
          return;
        }

        const token = localStorage.getItem('access_token');
        if (token) {
          try {
            const response = await authAPI.getProfile();
            setUser(response.data);
            localStorage.setItem('cached_user_profile', JSON.stringify(response.data));
            
            // Use language from database
            const languageToUse = response.data.preferred_language || 'en';
            
            i18n.changeLanguage(languageToUse);
            
            // Set RTL for Arabic
            if (languageToUse === 'ar') {
              document.documentElement.setAttribute('dir', 'rtl');
            } else {
              document.documentElement.setAttribute('dir', 'ltr');
            }
          } catch (error) {
            if (!navigator.onLine) {
              const cachedUser = localStorage.getItem('cached_user_profile');
              if (cachedUser) {
                try {
                  const parsed = JSON.parse(cachedUser);
                  setUser(parsed);
                  const languageToUse = parsed.preferred_language || 'en';
                  i18n.changeLanguage(languageToUse);
                  if (languageToUse === 'ar') {
                    document.documentElement.setAttribute('dir', 'rtl');
                  } else {
                    document.documentElement.setAttribute('dir', 'ltr');
                  }
                } catch (e) {
                  // ignore parse errors
                }
              }
            } else {
              try {
                localStorage.clear();
              } catch (e) {
                console.warn('Cannot clear localStorage');
              }
            }
          }
        }
      } catch (storageError) {
        console.warn('localStorage error:', storageError);
        // Continue without localStorage - user will just need to login again
      }
      
      setLoading(false);
    };

    loadUser();
  }, [i18n]);

  const login = async (username, password) => {
    try {
      console.log('🔍 Login attempt:', { username });
      const response = await authAPI.login(username, password);
      console.log('✓ Login response received:', response.data);
      
      const { access, refresh, user: userData } = response.data;
      
      if (!userData) {
        throw new Error('No user data in login response');
      }
      
      console.log('📝 User data:', userData);
      
      try {
        localStorage.setItem('access_token', access);
        localStorage.setItem('refresh_token', refresh);
        localStorage.setItem('cached_user_profile', JSON.stringify(userData));
      } catch (storageError) {
        console.warn('Cannot save to localStorage (might be in private browsing):', storageError);
        // Continue anyway - user will stay logged in until page refresh
      }
      
      setUser(userData);
      
      // Use user's preferred language from database
      const userLanguage = userData.preferred_language || 'en';
      i18n.changeLanguage(userLanguage);
      
      // Set RTL for Arabic
      if (userLanguage === 'ar') {
        document.documentElement.setAttribute('dir', 'rtl');
      } else {
        document.documentElement.setAttribute('dir', 'ltr');
      }
      
      console.log('✓ Login successful:', userData.username);
      return userData;
    } catch (error) {
      console.error('✗ Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          try {
            await authAPI.logout(refreshToken);
          } catch (logoutError) {
            // Ignore logout errors - token might be expired or invalid
            // User will still be logged out locally
          }
        }
      } catch (error) {
        // Ignore localStorage errors during logout
      }
    } finally {
      try {
        localStorage.clear();
      } catch (error) {
        console.warn('Cannot clear localStorage');
      }
      setUser(null);
      document.documentElement.setAttribute('dir', 'ltr');
    }
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    return response.data;
  };

  const updateUser = (updatedData) => {
    setUser({ ...user, ...updatedData });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, register, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
